/** Auth continuations belong to a tab, never a shared localStorage slot. */
export const AUTH_REDIRECT_KEY = "primepoint_auth_redirect";
export function safeAuthReturn(value: string | null): string | null {
    if (typeof value !== "string" || !value) return null;
    try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin || !["http:", "https:"].includes(url.protocol))
            return null;
        // URL normalization can turn same-origin /..//host into //host.
        // Returning that as a relative redirect would leave this origin.
        if (url.pathname.startsWith("//")) return null;
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}
/** A backslash (a slash to a special-scheme URL parser, not to a non-special one such as
 *  `capacitor:`) or a C0 control or DEL (which the parser drops or mangles): either makes one
 *  string parse differently on different origins, so neither belongs in an app path. */
// eslint-disable-next-line no-control-regex -- the control characters are what this rejects
const AMBIGUOUS_PATH_CHARACTER = /[\\\u0000-\u001f\u007f]/;

/** Whether a decoded path starts with two slashes (or a slash and a backslash): a path that
 *  something decoding it once (a router, a server redirect) would read as `//host`. */
function decodesToAuthority(pathname: string): boolean {
    try {
        return /^[/\\]{2}/.test(decodeURIComponent(pathname));
    } catch {
        // Malformed escapes decode to nothing; the raw path was already checked.
        return false;
    }
}

/** `path` as a path on whatever origin this page has, or null when following it could leave that
 *  origin (`//host`, `/\\host`, a `//` made by tabs or dot segments, `/%2F%2Fhost`) or it holds a
 *  backslash or a control character. Unlike `safeAuthReturn` it needs no http(s) origin: the iOS
 *  bundle runs at `capacitor://localhost`. */
export function samePagePath(path: string): string | null {
    if (!path.startsWith("/") || AMBIGUOUS_PATH_CHARACTER.test(path)) return null;
    try {
        // A fixed special-scheme base applies the browser's own normalization (dot segments)
        // whatever this page's scheme is.
        const url = new URL(path, "https://same-page.invalid");
        if (
            url.origin !== "https://same-page.invalid" ||
            url.pathname.startsWith("//") ||
            decodesToAuthority(url.pathname)
        ) {
            return null;
        }
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}
export function storeAuthReturn(value: string): void {
    const path = safeAuthReturn(value);
    if (!path) return;
    try {
        sessionStorage.setItem(AUTH_REDIRECT_KEY, path);
    } catch {
        /* URL redirect is the fallback. */
    }
}
export function consumeAuthReturn(): string | null {
    try {
        const value = sessionStorage.getItem(AUTH_REDIRECT_KEY);
        sessionStorage.removeItem(AUTH_REDIRECT_KEY);
        return safeAuthReturn(value);
    } catch {
        return null;
    }
}

const FLOW_PREFIX = "primepoint_auth_flow:";
// Destination metadata only, not an auth credential; tolerate delayed email delivery.
const FLOW_TTL_MS = 24 * 60 * 60 * 1000;
const FLOW_LIMIT = 24;

/** Magic-link-only continuations survive email opening a new tab. Register the optional
 * /authenticate?pp_flow={} callback alongside the bare URL in Stytch first. */
export async function withAuthReturnFlow<T>(
    callbackUrl: string,
    start: (url: string) => Promise<T>,
): Promise<T> {
    let key: string | null = null;
    let flowUrl = callbackUrl;
    try {
        const path = safeAuthReturn(sessionStorage.getItem(AUTH_REDIRECT_KEY));
        if (path) {
            const now = Date.now();
            const retained: { key: string; expires: number }[] = [];
            for (const old of Object.keys(localStorage).filter((k) => k.startsWith(FLOW_PREFIX))) {
                try {
                    const expires = JSON.parse(localStorage.getItem(old) ?? "null")?.expires;
                    if (
                        typeof expires === "number" &&
                        expires > now &&
                        expires <= now + FLOW_TTL_MS
                    )
                        retained.push({ key: old, expires });
                    else localStorage.removeItem(old);
                } catch {
                    localStorage.removeItem(old);
                }
            }
            retained
                .sort((a, b) => a.expires - b.expires)
                .slice(0, Math.max(0, retained.length - FLOW_LIMIT + 1))
                .forEach((flow) => localStorage.removeItem(flow.key));
            const id = Array.from(crypto.getRandomValues(new Uint32Array(4)), (n) =>
                n.toString(16).padStart(8, "0"),
            ).join("");
            key = FLOW_PREFIX + id;
            localStorage.setItem(key, JSON.stringify({ path, expires: now + FLOW_TTL_MS }));
            const url = new URL(callbackUrl);
            url.searchParams.set("pp_flow", id);
            flowUrl = url.toString();
        }
    } catch {
        /* Storage-denied browsers keep the existing same-tab continuation. */
    }
    try {
        return await start(flowUrl);
    } catch (error) {
        if (key) {
            try {
                localStorage.removeItem(key);
            } catch {
                /* Storage unavailable. */
            }
        }
        // Only a configuration rejection before authentication may retry bare.
        const failure = error as { error_type?: string; stytchError?: { error_type?: string } };
        if (
            flowUrl !== callbackUrl &&
            [failure?.error_type, failure?.stytchError?.error_type].includes(
                "query_params_do_not_match",
            )
        )
            return start(callbackUrl);
        throw error;
    }
}

export function restoreAuthReturnFlow(search: string): void {
    const ids = new URLSearchParams(search).getAll("pp_flow");
    if (ids.length !== 1 || !/^[a-f0-9]{32}$/.test(ids[0])) return;
    const key = FLOW_PREFIX + ids[0];
    try {
        const value = JSON.parse(localStorage.getItem(key) ?? "null");
        localStorage.removeItem(key);
        if (
            value &&
            typeof value.expires === "number" &&
            value.expires > Date.now() &&
            value.expires <= Date.now() + FLOW_TTL_MS
        ) {
            const path = safeAuthReturn(value.path);
            if (path) storeAuthReturn(path);
        }
    } catch {
        /* Unknown, expired and malformed flows cannot choose a project. */
    }
}
